import { formatCurrency } from "@/lib/formatting/currency";
import { keys } from "@/lib/i18n/portal";
import type { CouponView } from "@/lib/api/types";
import type { ShareMessageValues } from "@/shared/components/types/share";

export interface CouponShareMessage {
  messageKey: string;
  messageValues?: ShareMessageValues;
  messageValuesEn?: ShareMessageValues;
  messageValuesAr?: ShareMessageValues;
}

/**
 * Pick the discount-aware share caption for a coupon so the shared post reads
 * "20% off" / "20 EGP off" / "Free delivery" instead of a generic "a discount".
 * Fixed-amount coupons resolve the currency per locale (so the English post
 * shows `EGP 20` and the Arabic post `٢٠ ج.م`); percent/free-delivery captions
 * are locale-neutral in their values.
 */
export function getCouponShareMessage(coupon: CouponView): CouponShareMessage {
  if (coupon.kind === "FREE_DELIVERY") {
    return {
      messageKey: keys.coupons.shareMessageFreeDelivery,
      messageValues: { code: coupon.code },
    };
  }

  if (coupon.discountUnit === "PERCENT" && coupon.discountValue !== null) {
    return {
      messageKey: keys.coupons.shareMessagePercent,
      messageValues: { code: coupon.code, value: coupon.discountValue },
    };
  }

  if (coupon.discountUnit === "FIXED" && coupon.discountValue !== null) {
    return {
      messageKey: keys.coupons.shareMessageFixed,
      messageValuesEn: {
        code: coupon.code,
        amount: formatCurrency(coupon.discountValue, "en"),
      },
      messageValuesAr: {
        code: coupon.code,
        amount: formatCurrency(coupon.discountValue, "ar"),
      },
    };
  }

  // Discount not fully configured yet — fall back to the generic caption.
  return {
    messageKey: keys.coupons.shareMessage,
    messageValues: { code: coupon.code },
  };
}
